// Main App Component for Iron Forums

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { App as CapApp } from '@capacitor/app';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/useAuth';
import { platform } from './native';
import { sessionGate } from './lib/paymentGate';
import { hasGuestPrivileges, isAdminRole, isGodModeRole, isLeadershipRole } from './utils/roles';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import UpdateNudge from './components/UpdateNudge';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Snapshots from './pages/Snapshots';
import SnapshotDetail from './pages/SnapshotDetail';
import BlankSnapshot from './pages/BlankSnapshot';
import SnapshotWizard from './pages/SnapshotWizard';
import Commitments from './pages/Commitments';
import Jarvis from './pages/Jarvis';
import Messages from './pages/Messages';
import Prayers from './pages/Prayers';
import Notes from './pages/Notes';
import Members from './pages/Members';
import MemberDetail from './pages/MemberDetail';
import FacilitatorMemberDetail from './pages/FacilitatorMemberDetail';
import Meetings from './pages/Meetings';
import Settings from './pages/Settings';
import LeadershipDashboard from './pages/LeadershipDashboard';
import Forum from './pages/Forum';
import AccountabilityGroups from './pages/AccountabilityGroups';
import AdminMembers from './pages/AdminMembers';
import TeachingsAdmin from './pages/TeachingsAdminPhase6';
import Library from './pages/Library';
import PromptsAdmin from './pages/PromptsAdmin';
import EmailTemplatesAdmin from './pages/EmailTemplatesAdmin';
import SponsorsAdmin from './pages/SponsorsAdmin';
import Tickets from './pages/Tickets';
import MyRequests from './pages/MyRequests';
import Decisions from './pages/Decisions';
import WhatsNew from './pages/WhatsNew';
import RequirementsAdmin from './pages/RequirementsAdmin';
import ProfileOnboarding from './pages/ProfileOnboarding';
import AccountSetup from './pages/AccountSetup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LegalPage from './pages/LegalPage';
import WatchVideo from './pages/WatchVideo';
import SupportPage from './pages/SupportPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import LinkedInCallback from './pages/LinkedInCallback';
import CalendarCallback from './pages/CalendarCallback';
import Payment from './pages/Payment';
import Reimbursements from './pages/Reimbursements';
import Broadcast from './pages/Broadcast';
import MembersOnly from './pages/MembersOnly';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const gate = sessionGate(user);
  if (gate === 'onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  if (gate === 'payment') {
    return <Navigate to="/payment" replace />;
  }

  return (
    <Layout>
      <ErrorBoundary key={location.pathname} routeName={location.pathname}>{children}</ErrorBoundary>
    </Layout>
  );
}

// The server-derived requires_payment flag is authoritative on every platform.
// Web renders full checkout here; native renders a neutral blocked screen
// (no purchase links) inside the same Payment page.
function PaymentRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && !user.has_seen_onboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Members still in their grace month after a failed payment can restart early.
  if (user && !user.requires_payment && !user.payment_grace_until) {
    return <Navigate to="/" replace />;
  }

  return <ErrorBoundary routeName="/payment">{children}</ErrorBoundary>;
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <ErrorBoundary routeName="/onboarding">{children}</ErrorBoundary>;
}

// Capability-based route wrapper: 'leadership' admits facilitators and admins
// (including the combined Facilitator + Admin role); 'admin' admits admins only;
// 'god' admits only the platform-owner god_mode role.
function RoleRoute({
  children,
  requires,
}: {
  children: React.ReactNode;
  requires: 'leadership' | 'admin' | 'god';
}) {
  const { user } = useAuth();
  const allowed =
    requires === 'god'
      ? isGodModeRole(user?.role)
      : requires === 'admin'
        ? isAdminRole(user?.role)
        : isLeadershipRole(user?.role);

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function MembersOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (hasGuestPrivileges(user)) {
    return (
      <MembersOnly
        reason={user?.limit_app_access ? 'paused' : user?.payment_lapsed ? 'lapsed' : 'guest'}
      />
    );
  }
  return <>{children}</>;
}

// Legacy routes from before the unified /forum page. Preserve the ?forum=
// query so old deep links still open the right forum.
function LegacyRosterRedirect() {
  const location = useLocation();
  return <Navigate to={`/forum${location.search}`} replace />;
}

function LegacyMemberDetailRedirect() {
  const { forumId, memberId } = useParams();
  return <Navigate to={`/forum/${forumId}/member/${memberId}`} replace />;
}

function AndroidBackHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (platform !== 'android') return;

    const listener = CapApp.addListener('backButton', () => {
      if (location.pathname !== '/' && location.pathname !== '/login') {
        navigate(-1);
      } else {
        CapApp.exitApp();
      }
    });

    return () => {
      listener.then(handle => handle.remove());
    };
  }, [location.pathname, navigate]);

  return null;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <>
    <AndroidBackHandler />
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            <ErrorBoundary routeName="/login">
              <Login />
            </ErrorBoundary>
          )
        }
      />
      <Route
        path="/setup/:token"
        element={
          <ErrorBoundary routeName="/setup">
            <AccountSetup />
          </ErrorBoundary>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <ErrorBoundary routeName="/forgot-password">
            <ForgotPassword />
          </ErrorBoundary>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <ErrorBoundary routeName="/reset-password">
            <ResetPassword />
          </ErrorBoundary>
        }
      />
      <Route
        path="/watch/:slug"
        element={
          <ErrorBoundary routeName="/watch">
            <WatchVideo />
          </ErrorBoundary>
        }
      />
      <Route
        path="/privacy"
        element={
          <ErrorBoundary routeName="/privacy">
            <LegalPage doc="privacy" />
          </ErrorBoundary>
        }
      />
      <Route
        path="/terms"
        element={
          <ErrorBoundary routeName="/terms">
            <LegalPage doc="terms" />
          </ErrorBoundary>
        }
      />
      <Route
        path="/security"
        element={
          <ErrorBoundary routeName="/security">
            <LegalPage doc="security" />
          </ErrorBoundary>
        }
      />
      <Route
        path="/support"
        element={
          <ErrorBoundary routeName="/support">
            <SupportPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/delete-account"
        element={
          <ErrorBoundary routeName="/delete-account">
            <DeleteAccountPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <ProfileOnboarding />
          </OnboardingRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <PaymentRoute>
            <Payment />
          </PaymentRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forum"
        element={
          <ProtectedRoute>
            <Forum />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forum/:forumId/member/:memberId"
        element={
          <ProtectedRoute>
            <RoleRoute requires="leadership">
              <FacilitatorMemberDetail />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* The legacy Snapshot Group hub is retired; accountability groups are
          the group hub now. Old deep links (e.g. /my-group?section=prayers)
          land there too. */}
      <Route path="/my-group" element={<Navigate to="/accountability-groups" replace />} />
      <Route
        path="/accountability-groups"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <AccountabilityGroups />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/snapshots"
        element={
          <ProtectedRoute>
            <Snapshots />
          </ProtectedRoute>
        }
      />
      <Route
        path="/snapshots/new"
        element={
          <ProtectedRoute>
            <SnapshotWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/snapshots/blank"
        element={
          <ProtectedRoute>
            <BlankSnapshot />
          </ProtectedRoute>
        }
      />
      <Route
        path="/snapshots/:id"
        element={
          <ProtectedRoute>
            <SnapshotDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commitments"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <Commitments />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jarvis"
        element={
          <ProtectedRoute>
            <Jarvis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <Messages />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prayers"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <Prayers />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notes"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <Notes />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/members"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <Members />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/members/:id"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <MemberDetail />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      {/* Retired pages, folded into the unified /forum view. */}
      <Route path="/roster" element={<LegacyRosterRedirect />} />
      <Route path="/roster/:forumId/member/:memberId" element={<LegacyMemberDetailRedirect />} />
      <Route
        path="/meetings"
        element={
          <ProtectedRoute>
            <MembersOnlyRoute>
              <Meetings />
            </MembersOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Library />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route path="/forums" element={<Navigate to="/forum" replace />} />
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <RoleRoute requires="leadership">
              <LeadershipDashboard />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* Legacy URL from when this page was the group/forum dashboard. */}
      <Route path="/group" element={<Navigate to="/organization" replace />} />
      <Route
        path="/reimbursements"
        element={
          <ProtectedRoute>
            <RoleRoute requires="leadership">
              <Reimbursements />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/broadcast"
        element={
          <ProtectedRoute>
            <RoleRoute requires="admin">
              <Broadcast />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* The former org-wide "Analytics" page is now the org dashboard
          itself. Preserve the old /admin URL. */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Navigate to="/organization" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/teachings"
        element={
          <ProtectedRoute>
            <RoleRoute requires="leadership">
              <TeachingsAdmin />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/emails"
        element={
          <ProtectedRoute>
            <RoleRoute requires="admin">
              <EmailTemplatesAdmin />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prompts"
        element={
          <ProtectedRoute>
            <RoleRoute requires="god">
              <PromptsAdmin />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute>
            <RoleRoute requires="leadership">
              <AdminMembers />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/requirements"
        element={
          <ProtectedRoute>
            <RoleRoute requires="admin">
              <RequirementsAdmin />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sponsors"
        element={
          <ProtectedRoute>
            <RoleRoute requires="admin">
              <SponsorsAdmin />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={<Navigate to="/admin/tickets" replace />}
      />
      <Route
        path="/admin/tickets"
        element={
          <ProtectedRoute>
            <Tickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests"
        element={
          <ProtectedRoute>
            <MyRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new"
        element={
          <ProtectedRoute>
            <WhatsNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/decisions"
        element={
          <ProtectedRoute>
            <Decisions />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  // The LinkedIn OAuth popup loads this app at /linkedin/callback in a
  // same-origin window that shares localStorage with the opener. Mounting the
  // full tree (and AuthProvider) there fires a mount-time getMe() that the
  // popup's immediate window.close() aborts; the failure path would then wipe
  // the shared auth tokens and silently log the opener out (its next save 401s).
  // Render the callback in isolation so it only reads the query string and
  // postMessages back — it never touches auth state.
  if (typeof window !== 'undefined' && window.location.pathname === '/linkedin/callback') {
    return (
      <ErrorBoundary routeName="/linkedin/callback">
        <LinkedInCallback />
      </ErrorBoundary>
    );
  }

  // Same isolation rationale as LinkedIn: the Google/Microsoft calendar OAuth
  // popup loads this app at /calendar/callback in a same-origin window sharing
  // localStorage with the opener. Mounting AuthProvider here would fire a
  // mount-time getMe() that the popup's window.close() aborts, wiping the shared
  // auth tokens and logging the opener out. Render the callback in isolation so
  // it only reads the query string and postMessages back.
  if (typeof window !== 'undefined' && window.location.pathname === '/calendar/callback') {
    return (
      <ErrorBoundary routeName="/calendar/callback">
        <CalendarCallback />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary routeName="app-root">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <UpdateNudge />
          <Toaster
            position="top-right"
            containerStyle={{
              top: 'calc(env(safe-area-inset-top, 0px) + 4.25rem)',
            }}
            toastOptions={{
              duration: 3000,
              style: {
                background: '#043370',
                color: '#fff',
                borderRadius: '0.75rem',
                fontFamily: 'Quicksand, sans-serif',
              },
              success: { style: { background: '#043370' } },
              error: { style: { background: '#b91c1c' } },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
